









































import { computed, defineComponent, onMounted, onUnmounted, PropType, ref } from "@vue/composition-api";
import { Vue } from "vue/types/vue";
import { LikeSubjectType } from "@scrile/api-provider/dist/api/LikeProvider";
import { LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { TipSubjectType } from "@scrile/api-provider/dist/api/TipProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { Message } from "@scrile/api-provider/dist/projects/scrileConnect/MessagesProvider";
import { checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import useKeepCustomScrollPosition from "@/hooks/useKeepCustomScrollPosition";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { EVENTS } from "@/const";
import BaseIconButton from "@/components/BaseIconButton";
import BaseSendMessage from "@/components/BaseSendMessage";
import CustomScroll from "@/components/CustomScroll";
import SendMessageController from "@/components/SendMessageController";
import TipController from "@/components/TipController";
import TipIcon from "@/components/TipIcon";
import StreamMessage from "../StreamMessage";

export default defineComponent({
  name: "StreamChat",
  components: {
    SendMessageController,
    BaseSendMessage,
    StreamMessage,
    CustomScroll,
    BaseIconButton,
    TipController,
    TipIcon,
  },
  props: {
    threadId: { type: String, required: true },
    producer: { type: Object as PropType<UserPublicData>, required: true },
    token: { type: String, required: true },
  },
  setup(props) {
    const { authUser } = useAuthUser();

    let unsubscribe = () => {};
    const messageListNode = ref<Vue | undefined>();
    const value = ref("");
    const scrollbarWidth = ref(0);
    const sending = ref(false);
    const messageList = ref<Message[]>([]);
    const { shouldScrollToBottom, scrollNode } = useKeepCustomScrollPosition(messageListNode, { behavior: "smooth" });

    const canTip = computed(() => config.featureTippingLive && !(value.value.length > 0));
    const tipSubject = computed(() => ({
      subjectId: props.producer.id,
      subjectType: TipSubjectType.LIVE,
    }));

    const checkIsAdmin = (user: LiveChatUser) => {
      return checkRole(user?.user?.role ?? "", "admin");
    };

    const onNewMessageReceived = (message: Message) => {
      messageList.value = messageList.value.concat([message]);
      if (!scrollNode.value) return;
      const bottomScrollTopPosition = scrollNode.value.scrollHeight - scrollNode.value.offsetHeight;
      if (message.senderId === authUser.value?.id || bottomScrollTopPosition - scrollNode.value.scrollTop <= 100) {
        shouldScrollToBottom.value = true;
      }
    };

    const createJoinMessage = (newUser?: LiveChatUser) => {
      if (!newUser || checkIsAdmin(newUser)) return;
      const joinMessageId = new Date().getTime().toFixed(0);
      const joinMessage: Message = {
        id: joinMessageId,
        senderId: "0",
        senderUser: newUser.user,
        text: $t("{0} joined", [newUser.user ? newUser.user.screenName : $t("Guest user #{0}", [newUser.id])]),
        details: { type: "join" },
        threadId: "",
        time: new Date().toISOString(),
        link: null,
        attachment: [],
        likes: {
          subject: {
            subjectType: LikeSubjectType.MESSAGE,
            subjectId: joinMessageId,
          },
          count: 0,
          liked: false,
        },
      };
      onNewMessageReceived(joinMessage);
    };
    const subscribeLiveChatNewMessages = async () => {
      const subscription = await providers.LivechatsProvider.subscribeMessageReceived(
        props.token,
        onNewMessageReceived
      );
      unsubscribe = () => subscription.unsubscribe();
    };

    onMounted(() => {
      subscribeLiveChatNewMessages();
      emitter.on<LiveChatUser>(EVENTS.STREAM_USER_JOIN, createJoinMessage);
    });

    onUnmounted(() => {
      emitter.off<LiveChatUser>(EVENTS.STREAM_USER_JOIN, createJoinMessage);
      return unsubscribe();
    });

    return { messageList, sending, messageListNode, scrollbarWidth, value, canTip, tipSubject };
  },
});
